import { X } from "lucide-react";

export interface ActiveFilterPill {
  key: string;
  label: string;
}

interface ActiveFilterPillsProps<T extends ActiveFilterPill> {
  pills: T[];
  onRemove: (pill: T) => void;
  onClearAll: () => void;
}

/** "Clear all" only shows once there is more than one pill to clear (EPD-12259). */
const CLEAR_ALL_MIN_PILLS = 2;

/**
 * Removable pills for the active Marketplace filters, shown below the search
 * bar (EPD-12259). Renders nothing when no filter is active.
 */
export function ActiveFilterPills<T extends ActiveFilterPill>({
  pills,
  onRemove,
  onClearAll,
}: ActiveFilterPillsProps<T>) {
  if (pills.length === 0) return null;

  return (
    <div className="mt-3.5 flex flex-wrap items-center gap-2">
      {pills.map((pill) => (
        <span
          key={pill.key}
          className="inline-flex items-center gap-1.5 rounded-full bg-muted py-1.5 pr-2 pl-3.5 text-[13px] font-semibold text-foreground"
        >
          {pill.label}
          <button
            type="button"
            aria-label={`Remove ${pill.label} filter`}
            onClick={() => onRemove(pill)}
            className="cursor-pointer rounded-full text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <X className="size-[13px]" strokeWidth={2} aria-hidden="true" />
          </button>
        </span>
      ))}
      {pills.length >= CLEAR_ALL_MIN_PILLS && (
        <button
          type="button"
          onClick={onClearAll}
          className="cursor-pointer rounded-sm px-1 py-1.5 text-[13px] font-semibold text-ring focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          Clear all
        </button>
      )}
    </div>
  );
}
